import React, { useState, useMemo, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Plus, Zap, Check, Users, ShieldAlert, ShieldCheck, Search, Filter } from "lucide-react";
import toast from "react-hot-toast";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";

// Structured Symbol Selector Constants & Utility
const EXCHANGES = ["MCX", "NYMEX", "COMEX", "NSE"];
const INSTRUMENTS = ["CRUDEOIL", "GOLD", "SILVER", "NATURALGAS", "USDINR"];
const SYMBOL_TYPES = [
    { value: "FUT", label: "FUT" },
    { value: "CE", label: "CE" },
    { value: "PE", label: "PE" },
];
const MONTHS = [
    { name: "JAN", num: 1, code: "F" },
    { name: "FEB", num: 2, code: "G" },
    { name: "MAR", num: 3, code: "H" },
    { name: "APR", num: 4, code: "J" },
    { name: "MAY", num: 5, code: "K" },
    { name: "JUN", num: 6, code: "M" },
    { name: "JUL", num: 7, code: "N" },
    { name: "AUG", num: 8, code: "Q" },
    { name: "SEP", num: 9, code: "U" },
    { name: "OCT", num: 10, code: "V" },
    { name: "NOV", num: 11, code: "X" },
    { name: "DEC", num: 12, code: "Z" },
];
const YEARS = ["26", "27", "28", "29", "30"];

const COMEX_NYMEX_CODES = {
    CRUDEOIL: { fut: "CL", opt: "LO" },
    GOLD: { fut: "GC", opt: "OG" },
    SILVER: { fut: "SI", opt: "SO" },
    NATURALGAS: { fut: "NG", opt: "ON" },
    USDINR: { fut: "USD", opt: "USD" },
};

const COMEX_NYMEX_STRIKE_SCALES = {
    CRUDEOIL: 100,    // e.g. 103 -> 10300
    NATURALGAS: 1000, // e.g. 4.7 -> 4700
    GOLD: 1,          // e.g. 2650 -> 2650
    SILVER: 100,      // e.g. 30 -> 3000
    USDINR: 1,
};

const getMonthCode = (month) => {
    const monthUpper = (month || "OCT").toUpperCase();
    const found = MONTHS.find((m) => m.name === monthUpper);
    return found ? found.code : "V";
};

const buildSymbol = ({
    exchange = "MCX",
    instrument = "CRUDEOIL",
    symbolType = "FUT",
    expiryMonth = "OCT",
    expiryYear = "26",
    strike = "",
}) => {
    const exUpper = (exchange || "MCX").toUpperCase();
    const instUpper = (instrument || "CRUDEOIL").toUpperCase();
    const typeUpper = (symbolType || "FUT").toUpperCase();
    const monthUpper = (expiryMonth || "OCT").toUpperCase();
    const yearStr = (expiryYear || "26").toString();

    const isComexNymex = exUpper === "NYMEX" || exUpper === "COMEX";

    if (isComexNymex) {
        const monthCode = getMonthCode(monthUpper);
        const yearSingleDigit = yearStr.slice(-1);
        const yearTwoDigits = yearStr.padStart(2, "0").slice(-2);
        const codes = COMEX_NYMEX_CODES[instUpper] || { fut: instUpper, opt: instUpper };

        if (typeUpper === "FUT") {
            const futYear = instUpper === "NATURALGAS" ? yearTwoDigits : yearSingleDigit;
            return `${exUpper}:${codes.fut}${monthCode}${futYear}`;
        } else {
            const cpLetter = typeUpper === "CE" ? "C" : "P";
            const scale = COMEX_NYMEX_STRIKE_SCALES[instUpper] || 1;
            const strikeNum = Number(strike) || 0;
            const scaledStrike = Math.round(strikeNum * scale);
            return `${exUpper}:${codes.opt}${monthCode}${yearSingleDigit} ${cpLetter}${scaledStrike}`;
        }
    } else {
        const yearTwoDigits = yearStr.padStart(2, "0").slice(-2);

        if (typeUpper === "FUT") {
            return `${exUpper}:${instUpper}${yearTwoDigits}${monthUpper}FUT`;
        } else {
            const strikeClean = strike.toString().trim();
            return `${exUpper}:${instUpper}${yearTwoDigits}${monthUpper}${strikeClean}${typeUpper}`;
        }
    }
};

const SymbolLtpBadge = React.memo(({ symbol }) => {
    const symUpper = (symbol || "").toUpperCase();
    const ltp = useLtpStore((state) => (symUpper ? state.ltps[symUpper] || state.ltps[symUpper.replace(":", "")] : null));
    return (
        <span className="text-sm font-mono font-extrabold text-blue-400 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded">
            {ltp ? `₹${Number(ltp).toFixed(2)}` : "LTP: -"}
        </span>
    );
});

const RmsAddLegModal = ({ isOpen, onClose, basketId, participantClientIds = [], users = [], onSuccess }) => {
    const [exchange, setExchange] = useState("MCX");
    const [instrument, setInstrument] = useState("CRUDEOIL");
    const [symbolType, setSymbolType] = useState("FUT");
    const [expiryMonth, setExpiryMonth] = useState("OCT");
    const [expiryYear, setExpiryYear] = useState("26");
    const [strike, setStrike] = useState("");

    const [side, setSide] = useState("1"); // 1 = BUY, -1 = SELL
    const [qty, setQty] = useState("1");
    const [orderType] = useState("LIMIT");
    const [limitPrice, setLimitPrice] = useState("");
    const [broker, setBroker] = useState("fyers");
    const [isVirtual, setIsVirtual] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Client Selection State (PledgePlus Style)
    const [selectedClients, setSelectedClients] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [brokerFilter, setBrokerFilter] = useState("ALL");

    // Compute generated symbol dynamically
    const symbol = useMemo(() => {
        return buildSymbol({
            exchange,
            instrument,
            symbolType,
            expiryMonth,
            expiryYear,
            strike,
        });
    }, [exchange, instrument, symbolType, expiryMonth, expiryYear, strike]);

    const isOption = symbolType === "CE" || symbolType === "PE";

    // Filter available clients belonging to this basket or active users, enriched with details
    const eligibleClients = useMemo(() => {
        let clientIds = [];
        if (participantClientIds && participantClientIds.length > 0) {
            clientIds = participantClientIds.filter(cid => cid && cid !== "VIRTUAL");
        } else if (users && users.length > 0) {
            clientIds = users
                .filter(u => u && (u.status || "").toLowerCase() === "active")
                .map(u => u.client_id || u.clientId)
                .filter(Boolean);
        }

        return clientIds.map(cid => {
            const u = users?.find(user => (user.client_id || user.clientId) === cid);
            return {
                client_id: cid,
                clientId: cid,
                name: u?.name || cid,
                broker: u?.broker || "fyers",
                label: u?.label || "",
            };
        });
    }, [participantClientIds, users]);

    // Select all basket clients by default
    useEffect(() => {
        if (eligibleClients.length > 0) {
            setSelectedClients(eligibleClients.map(c => c.clientId));
        }
    }, [eligibleClients]);

    const uniqueLabels = useMemo(() => {
        const labels = new Set();
        eligibleClients.forEach(c => {
            if (c.label) labels.add(c.label);
        });
        return Array.from(labels).sort();
    }, [eligibleClients]);

    const filteredClients = useMemo(() => {
        let list = eligibleClients;
        if (brokerFilter !== "ALL") {
            list = list.filter(c => (c.broker || "").toLowerCase() === brokerFilter.toLowerCase());
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(c =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.clientId || c.client_id || "").toLowerCase().includes(q) ||
                (c.label || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [eligibleClients, brokerFilter, searchQuery]);

    if (!isOpen || !basketId) return null;

    const toggleClientSelection = (cid) => {
        setSelectedClients((prev) =>
            prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]
        );
    };

    const toggleSelectAll = () => {
        const visibleCids = filteredClients.map(c => c.clientId).filter(Boolean);
        const allSelected = visibleCids.every(id => selectedClients.includes(id));

        if (allSelected) {
            setSelectedClients(prev => prev.filter(id => !visibleCids.includes(id)));
        } else {
            const newSet = new Set([...selectedClients, ...visibleCids]);
            setSelectedClients(Array.from(newSet));
        }
    };

    const toggleLabelSelection = (label) => {
        const labelClients = filteredClients.filter(c => c.label === label);
        if (labelClients.length === 0) return;

        const labelCids = labelClients.map(c => c.clientId);
        const allSelected = labelCids.every(id => selectedClients.includes(id));

        if (allSelected) {
            setSelectedClients(prev => prev.filter(id => !labelCids.includes(id)));
        } else {
            const newSet = new Set([...selectedClients, ...labelCids]);
            setSelectedClients(Array.from(newSet));
        }
    };

    const handleVirtualToggle = (e) => {
        const checked = e.target.checked;
        setIsVirtual(checked);
        if (checked) {
            setBroker("virtual");
        } else {
            setBroker("fyers");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!symbol.trim()) {
            toast.error("Please enter a valid symbol.");
            return;
        }
        if (isOption && (!strike || isNaN(Number(strike)))) {
            toast.error(`Please enter a valid strike price for ${symbolType} options.`);
            return;
        }
        if (!limitPrice || isNaN(Number(limitPrice)) || Number(limitPrice) <= 0) {
            toast.error("Please enter a valid Limit Price.");
            return;
        }
        if (selectedClients.length === 0) {
            toast.error("Please select at least one client for order placement.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                basket_id: basketId,
                is_virtual: isVirtual,
                clients: selectedClients,
                leg: {
                    symbol: symbol.trim().toUpperCase(),
                    exchange: exchange.trim().toUpperCase(),
                    side: Number(side),
                    qty: Number(qty) || 1,
                    orderType: orderType,
                    limitPrice: Number(limitPrice),
                    broker: isVirtual ? "virtual" : broker,
                    is_virtual: isVirtual,
                }
            };

            const res = await apiClient.post("rms-arb/add-leg", payload);
            if (res.data && res.data.status === 200) {
                toast.success(`Successfully added leg for ${selectedClients.length} client(s) to basket ${basketId}!`);
                if (onSuccess) onSuccess();
                onClose();
            } else {
                toast.error(res.data?.message || "Failed to add leg to basket.");
            }
        } catch (err) {
            console.error("Error adding leg to basket:", err);
            toast.error(err.response?.data?.message || err.message || "Network error while adding leg.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-fade-in">
            <div className="bg-[#1a1d29] border border-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col text-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-[#13151f]">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-indigo-500/20 border border-indigo-500/30">
                            <Plus className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white tracking-wide flex items-center gap-2">
                                Add Leg to Basket
                                {isVirtual && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                        VIRTUAL LEG
                                    </span>
                                )}
                            </h2>
                            <p className="text-xs text-gray-400 font-mono">Basket ID: {basketId}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form - Side by Side 2 Column Layout */}
                <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-hidden p-6 flex flex-col lg:flex-row gap-6">

                    {/* Left Column: Leg Inputs & Virtual Switch */}
                    <div className="lg:w-1/2 space-y-4 flex flex-col min-h-0 overflow-y-auto pr-2 sleek-scrollbar">
                        {/* Generated Symbol & Live LTP Display Banner */}
                        <div className="flex items-center justify-between bg-[#11131c] p-3.5 rounded-xl border border-gray-800/80 gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 font-medium">Symbol:</span>
                                <span className="text-sm font-mono font-bold text-amber-300 px-3 py-1 bg-gray-900 border border-gray-700 rounded">
                                    {symbol || "N/A"}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 font-medium">Live LTP:</span>
                                <SymbolLtpBadge symbol={symbol} />
                            </div>
                        </div>

                        {/* Symbol Selection Parameters */}
                        <div className="grid grid-cols-3 gap-3 bg-[#11131c] p-4 rounded-xl border border-gray-800/80">
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Exchange</label>
                                <select
                                    value={exchange}
                                    onChange={(e) => setExchange(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                >
                                    {EXCHANGES.map((ex) => (
                                        <option key={ex} value={ex}>{ex}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Instrument</label>
                                <select
                                    value={instrument}
                                    onChange={(e) => setInstrument(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                >
                                    {INSTRUMENTS.map((inst) => (
                                        <option key={inst} value={inst}>{inst}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Type</label>
                                <select
                                    value={symbolType}
                                    onChange={(e) => setSymbolType(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                >
                                    {SYMBOL_TYPES.map((st) => (
                                        <option key={st.value} value={st.value}>{st.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Expiry Month, Year & Strike */}
                        <div className="grid grid-cols-3 gap-3 bg-[#11131c] p-4 rounded-xl border border-gray-800/80">
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Expiry Month</label>
                                <select
                                    value={expiryMonth}
                                    onChange={(e) => setExpiryMonth(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                >
                                    {MONTHS.map((m) => (
                                        <option key={m.name} value={m.name}>
                                            {m.name} {exchange === "NYMEX" || exchange === "COMEX" ? `(${m.code})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Expiry Year</label>
                                <select
                                    value={expiryYear}
                                    onChange={(e) => setExpiryYear(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                >
                                    {YEARS.map((yr) => (
                                        <option key={yr} value={yr}>20{yr}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">
                                    Strike {isOption ? <span className="text-red-400">*</span> : "(N/A)"}
                                </label>
                                <input
                                    type="number"
                                    step="any"
                                    placeholder={isOption ? "e.g. 9100 / 103" : "N/A"}
                                    disabled={!isOption}
                                    value={isOption ? strike : ""}
                                    onChange={(e) => setStrike(e.target.value)}
                                    className={`w-full bg-[#1a1d29] border rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500 ${!isOption ? "opacity-40 cursor-not-allowed border-gray-800" : "border-gray-700"
                                        }`}
                                />
                            </div>
                        </div>

                        {/* Side, Qty & Limit Price */}
                        <div className="grid grid-cols-3 gap-3 bg-[#11131c] p-4 rounded-xl border border-gray-800/80">
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Order Side</label>
                                <select
                                    value={side}
                                    onChange={(e) => setSide(e.target.value)}
                                    className={`w-full border rounded-lg px-2.5 py-1.5 text-xs font-bold ${side === "1"
                                            ? "bg-blue-500/20 text-blue-400 border-blue-500/40"
                                            : "bg-red-500/20 text-red-400 border-red-500/40"
                                        }`}
                                >
                                    <option value="1">BUY (Long)</option>
                                    <option value="-1">SELL (Short)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Lots / Qty</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={qty}
                                    onChange={(e) => setQty(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-semibold focus:outline-none focus:border-indigo-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">Limit Price</label>
                                <input
                                    type="number"
                                    step="any"
                                    placeholder="Price"
                                    value={limitPrice}
                                    onChange={(e) => setLimitPrice(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
                                    required
                                />
                            </div>
                        </div>

                        {/* Virtual Toggle Switch Card */}
                        <div className={`p-3.5 rounded-xl border transition-all ${isVirtual
                                ? "bg-amber-950/20 border-amber-500/40 text-amber-300"
                                : "bg-[#11131c] border-gray-800/80 text-gray-300"
                            }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    {isVirtual ? (
                                        <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
                                    ) : (
                                        <ShieldCheck className="w-5 h-5 text-gray-400 shrink-0" />
                                    )}
                                    <div>
                                        <div className="text-xs font-bold text-white flex items-center gap-2">
                                            Execute as Virtual Leg?
                                            {isVirtual && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold">
                                                    VIRTUAL EXEC
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-0.5">
                                            Places order virtually for selected client(s) without sending to broker.
                                        </p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-2">
                                    <input
                                        type="checkbox"
                                        checked={isVirtual}
                                        onChange={handleVirtualToggle}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: PledgePlus-Style Client Selection Panel */}
                    <div className="lg:w-1/2 flex flex-col min-h-0 overflow-hidden space-y-3">
                        <div className="flex items-center justify-between border-b border-indigo-900/30 pb-2">
                            <h3 className="text-indigo-400 font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span>Target Clients ({selectedClients.length}/{eligibleClients.length} Selected)</span>
                            </h3>
                        </div>

                        {/* Search Bar & Broker Filter Dropdown */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search client by ID, name, label..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#0f1116] border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                                />
                            </div>
                            <select
                                value={brokerFilter}
                                onChange={(e) => setBrokerFilter(e.target.value)}
                                className="bg-[#0f1116] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 outline-none cursor-pointer focus:border-indigo-500"
                            >
                                <option value="ALL">All Brokers</option>
                                <option value="FYERS">Fyers</option>
                                <option value="ZERODHA">Zerodha</option>
                                <option value="DHAN_XTS">Dhan XTS</option>
                                <option value="DHAN_HQ">Dhan HQ</option>
                                <option value="NUBRA">Nubra</option>
                            </select>
                        </div>

                        {/* Quick Group / Label Badges */}
                        {uniqueLabels.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {uniqueLabels.map(label => {
                                    const labelClients = filteredClients.filter(c => c.label === label);
                                    if (labelClients.length === 0) return null;

                                    const selectedCount = labelClients.filter(c => selectedClients.includes(c.clientId)).length;
                                    const isAllSelected = selectedCount === labelClients.length && labelClients.length > 0;

                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            onClick={() => toggleLabelSelection(label)}
                                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all border flex items-center gap-1.5 ${isAllSelected
                                                    ? 'bg-indigo-600 border-indigo-500 text-white'
                                                    : selectedCount > 0
                                                        ? 'bg-indigo-900/40 border-indigo-700 text-indigo-200'
                                                        : 'bg-[#14161f] border-gray-700 text-gray-500 hover:border-gray-500'
                                                }`}
                                        >
                                            <span>{label}</span>
                                            <span className={`px-1 rounded text-[9px] ${isAllSelected ? 'bg-indigo-800 text-white' : 'bg-gray-800 text-gray-400'}`}>
                                                {selectedCount}/{labelClients.length}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Client Checkbox List Container */}
                        <div className="flex-1 min-h-0 bg-[#0f1116] rounded-xl border border-gray-800 overflow-hidden flex flex-col">
                            <div className="bg-[#14161f] px-3.5 py-2 flex items-center justify-between border-b border-gray-800">
                                <button
                                    type="button"
                                    onClick={toggleSelectAll}
                                    className="text-[10px] text-indigo-400 font-bold hover:text-indigo-300 uppercase tracking-wider"
                                >
                                    {filteredClients.length > 0 && filteredClients.every(c => selectedClients.includes(c.clientId))
                                        ? "Deselect All Visible"
                                        : "Select All Visible"}
                                </button>
                                <span className="text-[10px] text-gray-500 uppercase font-mono">
                                    {filteredClients.length} clients eligible
                                </span>
                            </div>

                            <div className="flex-1 overflow-y-auto sleek-scrollbar divide-y divide-gray-800/40">
                                {filteredClients.map((client) => {
                                    const cid = client.clientId;
                                    const isSelected = selectedClients.includes(cid);

                                    return (
                                        <div
                                            key={cid}
                                            onClick={() => toggleClientSelection(cid)}
                                            className={`px-3.5 py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-indigo-500/5 transition-colors ${isSelected ? 'bg-indigo-500/10' : ''
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-indigo-600 border-indigo-500' : 'border-gray-600'
                                                    }`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                                </div>

                                                <div>
                                                    <p className="text-white text-xs font-semibold">{client.name}</p>
                                                    <p className="text-gray-400 text-[11px] font-mono">
                                                        {cid} {client.broker ? `| ${client.broker.toUpperCase()}` : ""}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {filteredClients.length === 0 && (
                                    <div className="p-8 text-center">
                                        <Filter className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                                        <p className="text-gray-500 text-xs">No matching target clients found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-800 bg-[#13151f]">
                    <div className="text-xs text-gray-400">
                        Adding leg for <span className="text-white font-bold">{selectedClients.length}</span> client(s)
                        {isVirtual && <span className="text-amber-400 font-semibold ml-1">(Virtual Execution)</span>}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-semibold transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || selectedClients.length === 0}
                            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Zap className="w-4 h-4" />
                            <span>
                                {submitting ? "Adding Leg..." : `Add Leg for ${selectedClients.length} Client(s)`}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(RmsAddLegModal);
